import { PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { QueryClient } from 'react-query';
import { call, getContext, select, takeLatest } from 'redux-saga/effects';
import { ProjectId } from 'api/models';
import { Project, ProjectBaseline, ProjectPhase } from 'api/project';
import { GanttTask } from 'components/organisms/projects/project-plan/timeline';
import {
  computeSlack,
  convertToGanttStoreFormat,
  convertToGanttTask,
  formatDate,
  GanttType,
  getDueDateForGantt,
  getExtremeDates,
  getGanttPhaseId,
  getGanttPhaseList,
  getGanttTaskId,
  getGanttTaskListWithoutBaselines,
} from 'components/organisms/projects/project-plan/timeline/helpers';
import { sagaTaskDescriptor } from 'data';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import {
  setGanttRefAction,
  updateGanttDatesAction,
  updateGanttSlackAction,
} from 'data/projects/actions';
import {
  CreateDependencyActionPayload,
  CreateSubTaskSuccessActionPayload,
  deleteTaskSagaAction,
  dependencyCreatedAction,
  dependencyRemovedAction,
  FetchTaskActionPayload,
  removeAsSubtaskSuccessAction,
  UpdateTaskActionPayload,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { Task } from 'data/tasks/schema';
import { selectTasksByProjectId } from 'data/tasks/selectors';
import { createSubTaskSuccess } from 'data/tasks/slice';
import { getProjectBaselineQueryKey } from 'queries/project-baseline-mutation';
import { DateShiftUpdates, List } from 'utils/date-shift';

interface RemoveAsSubtaskPayload extends FetchTaskActionPayload {
  phaseId?: number;
}

function setGanttRefContext({ payload }: PayloadAction<any>) {
  sagaTaskDescriptor.setContext({
    ganttRef: payload,
  });
}

function* getGanttRefContext() {
  return yield getContext('ganttRef');
}

function* onUpdateTask({
  payload,
}: PayloadAction<UpdateTaskActionPayload>): Generator {
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }
  const { taskId, data } = payload;
  const { taskName, fields, startDate, dueDate, progress } = data;
  const ganttId = `${GanttType.TaskBar}-${taskId}`;
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(ganttId);
  const recordData = record.get('data');
  const recordDataUpdates = {} as GanttTask;

  if (taskName) {
    record.set({
      title: taskName,
      name: taskName,
    });
    recordDataUpdates.name = taskName;
  }
  /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */
  if (startDate || startDate === null) {
    record.set('isCalculatedStartDate', !startDate);
    record.set('startDate', startDate);
  }
  if (dueDate || dueDate === null) {
    record.set('isCalculatedEndDate', !dueDate);
    record.set('endDate', dueDate);
  }

  if (fields) {
    const updatedFieldIds = fields.map((_field) => _field.fieldId);
    const updatedFields = fields.map((_update) => ({
      ...recordData.fields.find((field) => field.fieldId === _update.fieldId),
      ..._update,
    }));

    recordDataUpdates.fields = [
      ...recordData.fields.filter(
        (_field) => !updatedFieldIds.includes(_field.fieldId),
      ),
      ...updatedFields,
    ];
  }
  if (progress) {
    recordDataUpdates.progress = progress;
    record.set('percentDone', progress);
  }

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });
}

const updateGanttDates = (
  ganttRef,
  ganttId,
  currentEntity,
  startDate,
  dueDate,
) => {
  const { taskStore, project } = ganttRef?.instance;
  const record = taskStore.getById(ganttId);

  if (!record) {
    return [];
  }
  const spanDates: Date[] = [];

  if (project.startDate && project.endDate) {
    let newProjectStartDate = project.startDate;
    let newProjectEndDate = project.endDate;

    if (startDate) {
      if (moment(startDate).isBefore(moment(project.startDate))) {
        newProjectStartDate = new Date(startDate);
      }
    }
    if (dueDate) {
      if (moment(dueDate).isAfter(moment(project.endDate))) {
        newProjectEndDate = new Date(dueDate);
      }
    }
    spanDates[0] = newProjectStartDate;
    spanDates[1] = newProjectEndDate;
    ganttRef.instance.setTimeSpan(newProjectStartDate, newProjectEndDate);
  }
  record.setStartDate(formatDate(startDate || currentEntity.startDate));
  record.setEndDate(getDueDateForGantt(dueDate || currentEntity.dueDate));

  return spanDates as [Date, Date];
};

function* onUpdateGanttDates({
  payload,
}: PayloadAction<{
  phaseUpdates: DateShiftUpdates;
  taskUpdates: DateShiftUpdates;
  phaseList: List;
  taskList: List;
  projectPhases: ProjectPhase[];
}>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }

  let extremeProjectPhaseDates: Date[] = [];
  const { phaseUpdates, taskUpdates, phaseList, taskList, projectPhases } =
    payload;

  projectPhases.forEach((phase) => {
    extremeProjectPhaseDates = getExtremeDates(
      extremeProjectPhaseDates as [Date, Date],
      [new Date(phase.startDate), new Date(phase.dueDate)],
    );
  });

  Object.values(phaseUpdates)?.forEach(({ startDate, dueDate, id }) => {
    const ganttId = getGanttPhaseId(id);

    const spanDates = updateGanttDates(
      ganttRef,
      ganttId,
      phaseList[id],
      startDate,
      dueDate,
    );

    extremeProjectPhaseDates = getExtremeDates(
      extremeProjectPhaseDates as [Date, Date],
      spanDates as [Date, Date],
    );
  });
  Object.values(taskUpdates)?.forEach(({ startDate, dueDate, id }) => {
    const ganttId = getGanttTaskId(id);

    const spanDates = updateGanttDates(
      ganttRef,
      ganttId,
      taskList[id],
      startDate,
      dueDate,
    );

    extremeProjectPhaseDates = getExtremeDates(
      extremeProjectPhaseDates as [Date, Date],
      spanDates as [Date, Date],
    );
  });
  ganttRef?.instance.setTimeSpan(
    extremeProjectPhaseDates[0],
    extremeProjectPhaseDates[1],
  );
}

function* onDependencyCreated({
  payload,
}: PayloadAction<Omit<CreateDependencyActionPayload, 'projectId'>>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskId, dependentTaskId } = payload;

  const { dependencyStore } = ganttRef?.instance;

  dependencyStore.add({
    id: `${taskId}-${dependentTaskId}`,
    fromEvent: `${GanttType.TaskBar}-${dependentTaskId}`,
    toEvent: `${GanttType.TaskBar}-${taskId}`,
  });
}

function* onDependencyRemoved({
  payload,
}: PayloadAction<Omit<CreateDependencyActionPayload, 'projectId'>>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskId, dependentTaskId } = payload;

  const { dependencyStore } = ganttRef?.instance;

  dependencyStore.remove(`${taskId}-${dependentTaskId}`);
}

const createEmptyBaselines = (baselines: ProjectBaseline[]) => {
  return baselines.map((_baseline) => ({
    startDate: null,
    endDate: null,
  }));
};

function* onCreateSubTask(
  action: StoreActionProps<CreateSubTaskSuccessActionPayload>,
): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { payload } = action;

  const { parentTask, task } = payload;
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(`${GanttType.TaskBar}-${parentTask}`);

  if (!record) {
    return;
  }
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const baselines = queryClient.getQueryData<ProjectBaseline[]>(
    getProjectBaselineQueryKey(task.project.projectId),
  );

  const recordData = record.get('data');
  const ganttTask = {
    ...convertToGanttTask(task as unknown as Task),
    baselines: baselines ? createEmptyBaselines(baselines) : [],
  };

  record.appendChild(
    convertToGanttStoreFormat(
      ganttTask,
      record.getStartDate(),
      recordData.rootIndex,
      recordData.currentLevel + 1,
    ),
  );
}

function* onDeleteTask({
  payload,
}: PayloadAction<FetchTaskActionPayload>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskId } = payload;
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);

  if (!record) {
    return;
  }
  const parentId = record.get('data').parent;
  const parentRecord =
    taskStore.getById(getGanttTaskId(parentId)) ||
    taskStore.getById(getGanttPhaseId(parentId));

  if (!parentRecord) {
    return;
  }
  parentRecord.removeChild(record);
}

function* onUpdateGanttSlack({
  payload,
}: PayloadAction<{
  projectId: number;
}>): Generator {
  const { projectId } = payload;
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskStore } = ganttRef?.instance;

  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));

  if (!project || !project.startDate || !project.dueDate) {
    return;
  }

  const { projectPhases } = project;
  const tasks = (yield select(
    selectTasksByProjectId(ProjectId(projectId)),
  )) as Task[];

  const _phaseList = getGanttPhaseList(projectPhases);
  const _taskList = getGanttTaskListWithoutBaselines(tasks);

  const [updatedPhaseList, updatedTaskList] = computeSlack(
    _phaseList,
    _taskList,
    project,
  );

  Object.values(updatedPhaseList).forEach(({ id, slack }) => {
    const record = taskStore.getById(`${GanttType.ProjectPhaseBar}-${id}`);

    if (record) {
      record.set('totalSlack', slack);
      record.data.data.slack = slack;
    }
  });

  Object.values(updatedTaskList).forEach(({ id, slack }) => {
    const record = taskStore.getById(`${GanttType.TaskBar}-${id}`);

    if (record) {
      record.set('totalSlack', slack);
      record.data.data.slack = slack;
    }
  });
}

function* onRemoveAsSubtask({
  payload,
}: PayloadAction<RemoveAsSubtaskPayload>): Generator {
  const { taskId, phaseId } = payload;

  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
  const recordData = record.get('data');

  if (!record) {
    return;
  }
  const parentId = record.get('data').parent;
  const parentRecord =
    taskStore.getById(getGanttTaskId(parentId)) ||
    taskStore.getById(getGanttPhaseId(parentId));

  if (!parentRecord) {
    return;
  }
  parentRecord.removeChild(record);

  const phaseRecord = taskStore.getById(getGanttPhaseId(phaseId as number));

  const phaseRecordData = phaseRecord.get('data');

  record.set('data', {
    ...recordData,
    currentLevel: phaseRecordData.currentLevel + 1,
    parent: phaseRecordData.id,
  });

  phaseRecord.appendChild(record);
}

export const watchProjectTimelineSaga = function* (): Generator {
  yield takeLatest(setGanttRefAction.type, setGanttRefContext);
  yield takeLatest(updateGanttDatesAction.type, onUpdateGanttDates);
  yield takeLatest(updateTaskSagaAction.type, onUpdateTask);
  yield takeLatest(dependencyCreatedAction.type, onDependencyCreated);
  yield takeLatest(dependencyRemovedAction.type, onDependencyRemoved);
  yield takeLatest(createSubTaskSuccess.type, onCreateSubTask);
  yield takeLatest(deleteTaskSagaAction.type, onDeleteTask);
  yield takeLatest(updateGanttSlackAction.type, onUpdateGanttSlack);
  yield takeLatest(removeAsSubtaskSuccessAction.type, onRemoveAsSubtask);
};
